/**
 * client/src/audio/mediaSession.ts — the lock screen, the AirPods squeeze, the car stereo.
 *
 * §5.7: **Media Session is mandatory**, and it is the difference between "a website" and "an
 * audiobook". It is also the only part of this product a listener uses with the screen off, which is
 * the stated use case, which makes it the only part that cannot be judged from a desktop browser.
 *
 * Three decisions in here are stated in the design and are easy to undo by accident:
 *
 * 1. **Metadata is chapter-scoped.** The lock screen says *"CHAPTER V. Advice from a Caterpillar"*,
 *    not `c005.p07`, and not the book title repeated three times.
 *
 * 2. **`nexttrack` / `previoustrack` never mean "page".** They are registered **only** when the app
 *    supplies handlers — an action handler that does nothing renders as a live button on the lock
 *    screen and in the car, and a dead button is worse than an absent one. What they DO is the
 *    listener's setting (`lib/listenPrefs.ts`): ±30 s by default, because in a car those are the
 *    steering-wheel buttons and "chapter" there jumps up to an hour with no screen to undo it; or
 *    chapter, the lock-screen convention, for those who want it.
 *
 * 3. **`setPositionState` is chapter-scoped too**, or the scrubber shows a 90-second track that
 *    restarts at every page boundary and is useless for the one thing a scrubber is for. We have the
 *    chapter timeline as prefix sums (§4.6), so we report `chapterOffsetSec + el.currentTime` against
 *    `chapterDurationSec` — on swap, seek, rate change and play/pause, and **not** every frame.
 *
 * ⚠️ `setPositionState` THROWS on an inconsistent state — position past duration, a non-finite
 * number, a rate of zero. It is called from a page swap, so an exception here does not produce a bad
 * scrubber, it produces a page that never turns. Everything is validated before the call and the call
 * itself is wrapped; a lock screen with a slightly stale scrubber is a cosmetic defect, and a book
 * that stops at the end of page four is not.
 *
 * Nothing here touches a global directly: `navigator.mediaSession`, `MediaMetadata` and
 * `navigator.wakeLock` all arrive as arguments, because all three are absent under `node --test` and
 * two of them are absent on browsers we support.
 */

// ─── the platform, as narrow interfaces ──────────────────────────────────────

/** The actions we register. A subset of the spec's list — the ones an audiobook actually has. */
export type MediaAction =
  | "play"
  | "pause"
  | "stop"
  | "seekbackward"
  | "seekforward"
  | "seekto"
  | "previoustrack"
  | "nexttrack";

/** What the browser hands an action handler. Every field is optional in the spec, so it is here too. */
export interface MediaActionDetails {
  action?: string;
  seekOffset?: number;
  seekTime?: number;
  fastSeek?: boolean;
}

export interface MediaImageInit {
  src: string;
  sizes: string;
  type: string;
}

export interface MediaMetadataInit {
  title: string;
  artist: string;
  album: string;
  artwork: MediaImageInit[];
}

export interface MediaPositionInit {
  duration: number;
  playbackRate: number;
  position: number;
}

/** `navigator.mediaSession`, structurally. A real one satisfies this. */
export interface MediaSessionLike {
  metadata: unknown;
  playbackState: "none" | "paused" | "playing";
  setActionHandler(action: MediaAction, handler: ((details: MediaActionDetails) => void) | null): void;
  setPositionState?(state?: MediaPositionInit): void;
}

/** `new MediaMetadata(init)` — injected because the constructor is a global and is absent in Node. */
export type MetadataFactory = (init: MediaMetadataInit) => unknown;

// ─── the cover, at five sizes ────────────────────────────────────────────────

/**
 * The widths `GET /api/v1/covers/:bookId.webp?w=` serves.
 *
 * ⚠️ **Copied from `server/src/routes/covers.ts`, which is authoritative.** It cannot be imported:
 * that module imports fastify, and a client bundle that pulls fastify in to learn five integers is
 * not a trade. The route validates `?w=` against its own list and answers **400** for anything else,
 * so a drift here is not a silent wrong-sized image — it is five artwork entries that 400, an empty
 * lock screen, and nothing in the console. If a width is added there, add it here. It belongs in
 * `shared/`, and there is no shared home for it today.
 */
export const COVER_WIDTHS = [64, 128, 256, 512, 1024] as const;

/**
 * The five artwork entries for a book.
 *
 * `sizes` is a **hint** and is written `${w}x${w}` although a book cover is not square — the route
 * serves the stored bytes at their own aspect and says so in its own header. iOS picks the largest it
 * can use and scales; declaring a square is the convention every player follows and no platform
 * validates. The alternative is fetching every cover's real dimensions to fill in a string nobody
 * reads, on the path that has to be ready before the screen locks.
 *
 * `type` is `image/webp` for the same reason the path ends in `.webp`: it is the shape of the URL,
 * not a promise about the encoding — the response carries the stored blob's real `Content-Type`.
 */
export function coverArtwork(bookId: string, hasCover: boolean): MediaImageInit[] {
  if (!hasCover) return [];
  const base = `/api/v1/covers/${encodeURIComponent(bookId)}.webp`;
  return COVER_WIDTHS.map((w) => ({ src: `${base}?w=${w}`, sizes: `${w}x${w}`, type: "image/webp" }));
}

// ─── what the lock screen says ───────────────────────────────────────────────

/**
 * The book-level half of the metadata. Set once when a book opens; the chapter half moves with the
 * narration.
 */
export interface NowPlayingBook {
  bookId: string;
  title: string;
  author: string | null;
  hasCover: boolean;
}

/** The handlers the app supplies. Every one is optional, and an absent one is an unregistered action. */
export interface MediaActions {
  play(): void;
  pause(): void;
  /** `nexttrack` — chapter or +30 s, per the listener's setting. Absent → not registered at all. */
  nextChapter?(): void;
  previousChapter?(): void;
  /**
   * Skip by seconds, when the target is outside the current page. The engine handles in-page skips
   * itself; this is only called when it cannot, and an absent handler means the skip clamps to the
   * page rather than silently doing nothing.
   */
  skip?(seconds: number): void;
  /** An absolute seek in CHAPTER seconds, when the target is not inside the current page. */
  seekToChapterSec?(sec: number): void;
  stop?(): void;
}

/** Audiobook convention, and what every podcast app on the same lock screen uses. */
export const SEEK_BACK_SEC = 15;
export const SEEK_FORWARD_SEC = 30;

// ─── the bridge ──────────────────────────────────────────────────────────────

/**
 * One object that owns `navigator.mediaSession`, so nothing else writes to it.
 *
 * Absent Media Session is a **supported state**, not a degraded one: every method is a no-op when
 * `session === null` and the engine never asks whether it is there. That is what makes the same code
 * run in `node --test`, in a browser that has no Media Session, and on the phone the product is for.
 */
export class MediaSessionBridge {
  private book: NowPlayingBook | null = null;
  private chapterTitle = "";
  private readonly registered = new Set<MediaAction>();
  /** Last state we successfully reported. Kept so a rejected update does not leave us lying. */
  private lastPosition: MediaPositionInit | null = null;

  constructor(
    private readonly session: MediaSessionLike | null,
    private readonly makeMetadata: MetadataFactory | null,
  ) {}

  get available(): boolean {
    return this.session !== null && this.makeMetadata !== null;
  }

  /** The book. Setting it re-renders the metadata with whatever chapter is current. */
  setBook(book: NowPlayingBook): void {
    this.book = book;
    this.render();
  }

  /**
   * The chapter. Called on every page swap; a no-op when the chapter has not changed, because
   * constructing a `MediaMetadata` re-fetches the artwork on some platforms and a page turn is not a
   * reason to hit the cover route again.
   */
  setChapter(title: string): void {
    if (title === this.chapterTitle) return;
    this.chapterTitle = title;
    this.render();
  }

  private render(): void {
    const s = this.session;
    const book = this.book;
    if (s === null || this.makeMetadata === null || book === null) return;
    try {
      s.metadata = this.makeMetadata({
        // Chapter-scoped: the chapter is the "track", which is what makes `nexttrack` mean something.
        title: this.chapterTitle === "" ? book.title : this.chapterTitle,
        artist: book.author ?? "",
        album: book.title,
        artwork: coverArtwork(book.bookId, book.hasCover),
      });
    } catch {
      /* metadata is decoration; a page turn is not */
    }
  }

  setPlaybackState(state: "none" | "paused" | "playing"): void {
    if (this.session === null) return;
    try {
      this.session.playbackState = state;
    } catch {
      /* ditto */
    }
  }

  /**
   * The chapter-scoped scrubber.
   *
   * Everything is checked before the call because the spec makes an inconsistent state a `TypeError`:
   * a non-finite duration, a position past the duration, or `playbackRate === 0` all throw, and this
   * runs inside a page swap. `position` is clamped rather than refused — a page whose measured
   * duration overshoots its chapter's prefix sum by a frame is a rounding artefact, not a reason to
   * blank the lock screen.
   */
  setPosition(p: MediaPositionInit): void {
    const s = this.session;
    if (s === null || typeof s.setPositionState !== "function") return;

    if (!Number.isFinite(p.duration) || p.duration <= 0) return;
    if (!Number.isFinite(p.position) || !Number.isFinite(p.playbackRate)) return;

    const state: MediaPositionInit = {
      duration: p.duration,
      playbackRate: p.playbackRate > 0 ? p.playbackRate : 1,
      position: p.position < 0 ? 0 : p.position > p.duration ? p.duration : p.position,
    };
    try {
      s.setPositionState(state);
      this.lastPosition = state;
    } catch {
      /* a stale scrubber beats a page that never turns */
    }
  }

  /** What we last told the platform. Tests read it; nothing in the engine does. */
  get position(): MediaPositionInit | null {
    return this.lastPosition;
  }

  /**
   * Register the actions.
   *
   * ⚠️ An action whose handler is absent is explicitly **cleared** (`setActionHandler(a, null)`), not
   * left alone. Handlers survive a re-registration, so a book with chapters followed by one without —
   * or a second call with a narrower set — would otherwise leave the previous book's `nexttrack`
   * live on the lock screen, pointing at a closure over a book that is no longer open.
   */
  bindActions(actions: MediaActions): void {
    const s = this.session;
    if (s === null) return;

    const set = (action: MediaAction, handler: ((d: MediaActionDetails) => void) | null): void => {
      try {
        s.setActionHandler(action, handler);
        if (handler === null) this.registered.delete(action);
        else this.registered.add(action);
      } catch {
        // Safari throws `NotSupportedError` for actions it does not implement rather than ignoring
        // them, and one unsupported action must not cost us the ones that work.
        this.registered.delete(action);
      }
    };

    set("play", () => actions.play());
    set("pause", () => actions.pause());
    set("stop", actions.stop ? () => actions.stop?.() : null);

    set("seekbackward", (d) => {
      const by = Number.isFinite(d.seekOffset) && (d.seekOffset ?? 0) > 0 ? (d.seekOffset as number) : SEEK_BACK_SEC;
      actions.skip?.(-by);
    });
    set("seekforward", (d) => {
      const by = Number.isFinite(d.seekOffset) && (d.seekOffset ?? 0) > 0 ? (d.seekOffset as number) : SEEK_FORWARD_SEC;
      actions.skip?.(by);
    });

    set(
      "seekto",
      actions.seekToChapterSec
        ? (d) => {
            if (!Number.isFinite(d.seekTime)) return;
            actions.seekToChapterSec?.(d.seekTime as number);
          }
        : null,
    );

    // Never page — and absent handlers leave the buttons off the lock screen entirely.
    set("nexttrack", actions.nextChapter ? () => actions.nextChapter?.() : null);
    set("previoustrack", actions.previousChapter ? () => actions.previousChapter?.() : null);
  }

  /** Which actions are live. Tests assert on this; it is also the honest answer for a diagnostic. */
  get actions(): MediaAction[] {
    return [...this.registered].sort();
  }

  /**
   * Hand the lock screen back. Called when the reader unmounts or the book closes.
   *
   * The metadata is cleared **and** every handler is unregistered: an `<audio>` element that has been
   * torn down leaves its Now Playing card up on iOS until something replaces it, and a live `play`
   * handler closing over a destroyed engine is how a lock-screen button starts throwing.
   */
  release(): void {
    const s = this.session;
    if (s === null) return;
    for (const action of [...this.registered]) {
      try {
        s.setActionHandler(action, null);
      } catch {
        /* nothing to do about a platform that refuses to forget */
      }
    }
    this.registered.clear();
    try {
      s.metadata = null;
      s.playbackState = "none";
    } catch {
      /* ditto */
    }
    this.lastPosition = null;
  }
}

// ─── the screen ──────────────────────────────────────────────────────────────

/** `WakeLockSentinel`, structurally. */
export interface WakeSentinelLike {
  readonly released: boolean;
  release(): Promise<void>;
  addEventListener(type: "release", handler: () => void): void;
}

/** `navigator.wakeLock`, structurally. */
export interface WakeLockApi {
  request(type: "screen"): Promise<WakeSentinelLike>;
}

/** The part of `document` this needs. */
export interface VisibilityHost {
  readonly visibilityState: "visible" | "hidden" | string;
  addEventListener(type: "visibilitychange", handler: () => void): void;
  removeEventListener(type: "visibilitychange", handler: () => void): void;
}

/**
 * "Keep the screen on while reading" — Screen Wake Lock, which shipped in **Safari 16.4**, contrary
 * to one proposal that assumed it had not (§5.7).
 *
 * **Default off**, always, and exposed as a labelled preference rather than inferred from playback:
 * this is a product for reading in bed, and an app that silently prevents a phone from sleeping is a
 * flat battery in the morning that nobody attributes to the book they fell asleep to.
 *
 * ⚠️ **The lock is released by the browser whenever the document is hidden and is never restored on
 * its own.** Switch apps and come back and the screen starts sleeping again, so the preference stops
 * working after the first interruption — with no error, no state change, and a toggle that still says
 * "on". Hence `attach()`: re-request on `visibilitychange → visible`. That listener *is* the feature.
 */
export class ScreenWakeLock {
  private sentinel: WakeSentinelLike | null = null;
  private wanted = false;
  private detach: (() => void) | null = null;
  /** Why the last request failed, for the settings row. Never thrown at the caller. */
  lastError: string | null = null;

  constructor(
    private readonly api: WakeLockApi | null,
    private readonly doc: VisibilityHost | null,
  ) {}

  get supported(): boolean {
    return this.api !== null;
  }

  /** Whether the user asked for it — not whether the lock is currently held. */
  get enabled(): boolean {
    return this.wanted;
  }

  /** Whether the OS is holding it right now. These two disagree while the tab is hidden, by design. */
  get held(): boolean {
    return this.sentinel !== null && !this.sentinel.released;
  }

  async enable(): Promise<boolean> {
    this.wanted = true;
    this.listen();
    return this.acquire();
  }

  async disable(): Promise<void> {
    this.wanted = false;
    const s = this.sentinel;
    this.sentinel = null;
    if (s !== null && !s.released) {
      try {
        await s.release();
      } catch {
        /* a lock the platform already dropped */
      }
    }
  }

  private async acquire(): Promise<boolean> {
    if (this.api === null || !this.wanted) return false;
    if (this.held) return true;
    // A request while hidden always rejects (`NotAllowedError`), which is exactly the case `attach()`
    // exists to retry — so it is not an error worth surfacing, it is a "not yet".
    if (this.doc !== null && this.doc.visibilityState !== "visible") return false;
    try {
      const s = await this.api.request("screen");
      s.addEventListener("release", () => {
        if (this.sentinel === s) this.sentinel = null;
      });
      this.sentinel = s;
      this.lastError = null;
      return true;
    } catch (err) {
      // Low battery and Low Power Mode both refuse. The toggle stays on and we retry when we are next
      // visible; telling the user "failed" for a condition that clears itself is noise.
      this.lastError = String(err);
      this.sentinel = null;
      return false;
    }
  }

  private listen(): void {
    if (this.detach !== null || this.doc === null) return;
    const doc = this.doc;
    const onVisible = (): void => {
      if (doc.visibilityState === "visible" && this.wanted && !this.held) void this.acquire();
    };
    doc.addEventListener("visibilitychange", onVisible);
    this.detach = () => doc.removeEventListener("visibilitychange", onVisible);
  }

  /** Stop retrying and drop the lock. Called on unmount. */
  async destroy(): Promise<void> {
    this.detach?.();
    this.detach = null;
    await this.disable();
  }
}

// ─── the browser's own objects, found once ───────────────────────────────────

/**
 * `navigator.mediaSession` if this browser has one, `null` otherwise.
 *
 * Feature-detected rather than version-detected: iOS 15 has Media Session but not
 * `setPositionState`, some desktop browsers have both, and `node --test` has neither. The bridge
 * treats every one of those as a supported configuration.
 */
export function browserMediaSession(): { session: MediaSessionLike | null; metadata: MetadataFactory | null } {
  if (typeof navigator === "undefined") return { session: null, metadata: null };
  const nav = navigator as unknown as { mediaSession?: MediaSessionLike };
  const session = nav.mediaSession ?? null;
  const metadata: MetadataFactory | null =
    typeof MediaMetadata === "undefined" ? null : (init) => new MediaMetadata(init);
  return { session, metadata };
}

/** `navigator.wakeLock` if this browser has one (Safari 16.4+, Chrome 84+), `null` otherwise. */
export function browserWakeLock(): WakeLockApi | null {
  if (typeof navigator === "undefined") return null;
  const nav = navigator as unknown as { wakeLock?: WakeLockApi };
  return nav.wakeLock ?? null;
}
